import { useState, useEffect, useRef } from 'react';
import { 
  Search, Loader2, X, Sparkles, User, Calendar, 
  FileText, TestTube, Pill, CreditCard, Stethoscope,
  ChevronRight, Clock, AlertCircle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';

const RESULT_ICONS = {
  patient: User,
  appointment: Calendar,
  prescription: Pill,
  lab_order: TestTube,
  visit: Stethoscope,
  billing: CreditCard,
  default: FileText
};

const RESULT_COLORS = {
  patient: 'bg-blue-100 text-blue-700',
  appointment: 'bg-green-100 text-green-700',
  prescription: 'bg-purple-100 text-purple-700',
  lab_order: 'bg-orange-100 text-orange-700',
  visit: 'bg-cyan-100 text-cyan-700',
  billing: 'bg-amber-100 text-amber-700',
  default: 'bg-slate-100 text-slate-700'
};

export default function SmartSearch({ onResultClick, className = "" }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const debounceRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('smart_search_recent');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch (e) {
        console.error('Failed to parse recent searches');
      }
    }
  }, []);

  // Load search suggestions on mount
  useEffect(() => {
    fetchSuggestions();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowResults(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await api.get('/ai/search-suggestions');
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    }
  };

  const saveRecentSearch = (searchQuery) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('smart_search_recent', JSON.stringify(updated));
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim() || searchQuery.length < 3) return;
    
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    
    try {
      const response = await api.post('/ai/smart-search', { query: searchQuery });
      if (response.data.success) {
        setResults(response.data.results || []);
        setShowResults(true);
        saveRecentSearch(searchQuery);
      } else {
        setError('Search failed');
        setResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    if (onResultClick) {
      onResultClick(result);
    }
  };

  const handleSuggestionClick = (example) => {
    setQuery(example);
    handleSearch(example);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  const ResultIcon = ({ type }) => {
    const Icon = RESULT_ICONS[type] || RESULT_ICONS.default;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <Search className="h-4 w-4 text-slate-400" />
        </div>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!query && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder="Search patients, appointments, lab results... (AI-powered)"
          className="pl-14 pr-20 h-12 text-base"
          data-testid="smart-search-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            onClick={() => handleSearch()}
            disabled={loading || query.length < 3}
            size="sm"
            className="h-8 bg-purple-600 hover:bg-purple-700"
            data-testid="smart-search-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </div>

      {/* Results/Suggestions Dropdown */}
      <AnimatePresence>
        {(showResults || showSuggestions) && (
          <motion.div
            ref={resultsRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
            data-testid="smart-search-results"
          >
            {/* Search Suggestions (when input is empty) */}
            {showSuggestions && !showResults && (
              <div className="max-h-[400px] overflow-y-auto">
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div className="p-3 border-b">
                    <h4 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Recent Searches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((search, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestionClick(search)}
                          className="px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700"
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Example Queries by Category */}
                <div className="p-3">
                  <h4 className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Try asking...
                  </h4>
                  <div className="space-y-3">
                    {suggestions.map((category, i) => (
                      <div key={i}>
                        <h5 className="text-xs font-medium text-slate-600 mb-1">{category.category}</h5>
                        <div className="flex flex-wrap gap-1">
                          {category.examples.slice(0, 3).map((example, j) => (
                            <button
                              key={j}
                              onClick={() => handleSuggestionClick(example)}
                              className="px-2 py-1 text-xs bg-purple-50 hover:bg-purple-100 rounded text-purple-700 transition-colors"
                            >
                              {example}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {showResults && (
              <>
                {/* Results Header */}
                <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
                  </span>
                  <button
                    onClick={() => setShowResults(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Results List */}
                <div className="max-h-[400px] overflow-y-auto">
                  {error ? (
                    <div className="p-4 text-center text-red-600 flex items-center justify-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  ) : results.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No results found</p>
                      <p className="text-sm mt-1">Try a different search query</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {results.map((result, index) => (
                        <button
                          key={`${result.type}-${result.id}-${index}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start gap-3"
                          data-testid={`search-result-${index}`}
                        >
                          <div className={`p-2 rounded-lg ${RESULT_COLORS[result.type] || RESULT_COLORS.default}`}>
                            <ResultIcon type={result.type} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">
                                {result.name || result.patient_name || result.prescription_number || result.order_number || result.invoice_number || result.visit_number || 'Record'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${RESULT_COLORS[result.type] || RESULT_COLORS.default}`}>
                                {result.type.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-2">
                              {result.status && (
                                <span className="capitalize">{result.status}</span>
                              )}
                              {result.phone && <span>{result.phone}</span>}
                              {result.scheduled_date && <span>{result.scheduled_date}</span>}
                              {result.scheduled_time && <span>{result.scheduled_time}</span>}
                              {result.test_name && <span>{result.test_name}</span>}
                              {result.total_amount && <span>${result.total_amount}</span>}
                              {result.chief_complaint && <span className="truncate max-w-[200px]">{result.chief_complaint}</span>}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
